// @ts-nocheck
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
// import { infoProps } from "@store/Themes/common/props/info"; // Greif
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";

/* Greif Custom Code - Start */
import AlertTriangleFill from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import { orderSubtitleSpacingStyles } from "@store/Themes/common/mixins/order";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import { infoProps } from "@store/Themes/common/props/info";
import { css } from "styled-components";
/* Greif Custom Code - End */

const Date = getWidgetComponent("OrderDetails/OrderDate");
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");
const Salesperson = getWidgetComponent("OrderDetails/OrderSalesperson");
const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const PaymentTerms = getWidgetComponent("OrderDetails/OrderDetailsPaymentTerms");
const PoNumber = getWidgetComponent("OrderDetails/OrderPo");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");
const BillingAddress = getWidgetComponent("OrderDetails/OrderDetailsBillingAddress");
const ShippingMethod = getWidgetComponent("OrderDetails/OrderDetailsShippingMethod");
const ShipmentPackages = getWidgetComponent("OrderDetails/OrderDetailsShipmentPackages");
const VatNumber = getWidgetComponent("OrderDetails/OrderDetailsVatNumber");
const VmiLocation = getWidgetComponent("OrderDetails/OrderDetailsVmiLocation");
// Greif
const Carrier = getWidgetComponent("OrderDetails/OrderDetailsCarrier");
const DeliveryTerms = getWidgetComponent("OrderDetails/OrderDetailsDeliveryTerms");
const Warehouse = getWidgetComponent("OrderDetails/OrderDetailsWarehouse");

interface Props extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
}

export interface OrderDetailsInformationStyles {
    block?: BlockProps;
    orderNumberHeading?: TypographyProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    billingInformationTitle?: TypographyProps;
    // Greif
    billingShippingGridItem?: GridItemProps;
    webOrderNumberHeading?: TypographyProps;
    changeTooltipAlert?: TooltipPresentationProps;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    block: orderProps.block,
    orderNumberHeading: orderProps.subtitle,
    infoGridContainer: infoProps.grid?.container,
    infoGridItem: {
        ...infoProps.grid?.item,
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    notesGridItem: infoProps.grid?.fullItem, // Greif Custom Code
    shippingInformationTitle: orderProps.subtitle,
    billingInformationTitle: orderProps.subtitle,
    /* Greif Custom Code - Start */
    billingShippingGridItem: {
        width: [12, 6, 6, 4, 4],
    },
    webOrderNumberHeading: {
        forwardAs: "p",
        css: css`
            ${orderSubtitleSpacingStyles()}
            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.large};
                font-weight: ${theme.design.typography.weight.medium};
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },

    /* Greif Custom Code - End */
};

const styles = orderDetailsInformationStyles;

const OrderDetailsInformation: FC<Props> = ({ orderState: baseOrderState }) => {
    const { orderSettings, productSettings } = useSettingsCollection();
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    const order = orderState.value;
    /* Greif Custom Code - Start */
    const [orderChangePropriety, setOrderChangePropriety] = React.useState<any>();

    React.useEffect(() => {
        if (order?.properties.orderChanges) {
            setOrderChangePropriety(JSON.parse(order?.properties.orderChanges)?.orderLines.splice(-1)[0]);
        }
    }, [order?.properties.orderChanges]);
    /* Greif Custom Code - End */

    if (!order) {
        return null;
    }

    const showPoNumber = orderSettings.showPoNumber && order.customerPO;
    const showVatNumber = productSettings.enableVat && order.customerVatNumber;
    const showTerms = orderSettings.showTermsCode && order.terms;
    const showInformations =
        order.orderDate ||
        showPoNumber ||
        order.statusDisplay ||
        order.salesperson ||
        showVatNumber ||
        showTerms ||
        order.vmiLocationName ||
        // Greif Custom Code
        order.notes ||
        order.requestedDeliveryDateDisplay ||
        order.properties.carrier ||
        order.properties.deliveryTerms ||
        order.properties.warehouse;

    return (
        <OrderStateContext.Provider value={orderState}>
            <Block {...styles.block}>
                {/* Greif Custom Code - Start */}
                {order.erpOrderNumber && (
                    <Typography {...styles.orderNumberHeading}>
                        {translate("Greif Order Number: ")}
                        {order.erpOrderNumber}
                    </Typography>
                )}
                {order.properties.webOrderNumberDisplay && (
                    <Typography {...(order.erpOrderNumber ? styles.webOrderNumberHeading : styles.orderNumberHeading)}>
                        {translate("Order Upload Reference: ")}
                        {order.webOrderNumber}
                    </Typography>
                )}
                {/* Greif Custom Code - End */}
                {showInformations ? (
                    <GridContainer {...styles.infoGridContainer}>
                        {order.orderDate ? (
                            <GridItem {...styles.infoGridItem}>
                                <Date />
                            </GridItem>
                        ) : null}
                        {showPoNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <PoNumber />
                            </GridItem>
                        ) : null}
                        {order.statusDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <Status />
                            </GridItem>
                        ) : null}
                        {order.salesperson ? (
                            <GridItem {...styles.infoGridItem}>
                                <Salesperson />
                            </GridItem>
                        ) : null}
                        {/* Greif Custom Code - Start */}
                        {order.properties.warehouse ? (
                            <GridItem {...styles.infoGridItem}>
                                <Warehouse />
                            </GridItem>
                        ) : null}
                        {/* Greif Custom Code - End */}
                        {showVatNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <VatNumber />
                            </GridItem>
                        ) : null}
                        {order.vmiLocationName ? (
                            <GridItem {...styles.infoGridItem}>
                                <VmiLocation />
                            </GridItem>
                        ) : null}
                        {/* Greif Custom Code - Start */}
                        {order.requestedDeliveryDateDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <RequestedDate />
                            </GridItem>
                        ) : null}
                        {order.properties.carrier ? (
                            <GridItem {...styles.infoGridItem}>
                                <Carrier />
                            </GridItem>
                        ) : null}
                        {order.properties.deliveryTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <DeliveryTerms />
                            </GridItem>
                        ) : null}
                        {showTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <PaymentTerms />
                            </GridItem>
                        ) : null}
                        {/* Greif Custom Code - End */}
                        {order.notes ? (
                            <GridItem {...styles.notesGridItem}>
                                <Notes />
                            </GridItem>
                        ) : null}
                    </GridContainer>
                ) : null}
            </Block>
            <Block {...styles.block}>
                {/* Greif Custom Code - Start */}
                <GridContainer>
                    <GridItem {...styles.billingShippingGridItem}>
                        <BillingAddress />
                    </GridItem>
                    <GridItem {...styles.billingShippingGridItem}>
                        <ShippingAddress />
                    </GridItem>
                </GridContainer>
                {/* Greif Custom Code - End */}
            </Block>
            {/* Greif Removed - shippingInformationTitle */}
        </OrderStateContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Information",
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
    },
};

export default widgetModule;
